import type { HocuspocusProvider } from "@hocuspocus/provider";
import Collaboration from "@tiptap/extension-collaboration";
import CollaborationCaret from "@tiptap/extension-collaboration-caret";
import Image from "@tiptap/extension-image";
import {
  BulletList,
  ListItem,
  ListKeymap,
  OrderedList,
  TaskItem,
  TaskList,
} from "@tiptap/extension-list";
import { Table, TableCell, TableHeader, TableRow } from "@tiptap/extension-table";
import { EditorContent, Extension, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { CustomLink as Link, SHARED_STARTERKIT_OPTIONS } from "@workith/editor-schema";
import {
  ANONYMOUS_COLLABORATION_TOKEN,
  type CollaborativeUser,
  createRandomUser,
} from "@workith/shared";
import { cn } from "@workith/ui";
import css from "highlight.js/lib/languages/css";
import js from "highlight.js/lib/languages/javascript";
import plaintext from "highlight.js/lib/languages/plaintext";
import python from "highlight.js/lib/languages/python";
import ts from "highlight.js/lib/languages/typescript";
import html from "highlight.js/lib/languages/xml";
import { createLowlight } from "lowlight";
import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import DocumentStatus from "../document/DocumentStatus";
import { EnhancedCodeBlock } from "../editor/extensions/EnhancedCodeBlock";
import { EnsureTableCellContent } from "../editor/extensions/EnsureTableCellContent";
import { createFrontMatterExtensions } from "../editor/extensions/frontmatter/FrontMatterExtension";
import { MarkdownPaste, type MarkdownPasteOptions } from "../editor/extensions/MarkdownPaste";
import MenuBar from "../editor/MenuBar";
import CollaborationRecoveryBanner from "./CollaborationRecoveryBanner";
import ConnectionStatus, { CollaborationAnnouncer } from "./ConnectionStatus";
import {
  collaborationEditorReducer,
  collaborationEditorStatus,
  collaborationLocalChangeState,
  collaborationTerminalAlert,
  createCollaborationEditorState,
  isCollaborativeEditorEditable,
  shouldWarnBeforeUnload,
  useLatestRef,
  useUnloadWarning,
} from "./collaborationEditorState";
import {
  type CollaborationCloseInfo,
  type CollaborationCredential,
  type CollaborationProviderBundle,
  createCollaborationProvider,
} from "./collaborationProvider";
import { useCollaborationRecovery } from "./collaborationRecovery";
import { buildCollaborationUrl } from "./collaborationUrl";
import type { RejectedChangeSnapshot } from "./sectionedCollaborationProvider";
import UserList from "./UserList";

const lowlight = createLowlight();
lowlight.register("html", html);
lowlight.register("css", css);
lowlight.register("js", js);
lowlight.register("ts", ts);
lowlight.register("python", python);
lowlight.register("plaintext", plaintext);

const tabCatcher = Extension.create({
  name: "tabCatcher",
  addKeyboardShortcuts() {
    return {
      Tab: () => {
        if (this.editor.commands.sinkListItem("listItem")) {
          return true;
        }
        if (this.editor.commands.sinkListItem("taskItem")) {
          return true;
        }
        return false;
      },
      "Shift-Tab": () => {
        if (this.editor.commands.liftListItem("listItem")) {
          return true;
        }
        if (this.editor.commands.liftListItem("taskItem")) {
          return true;
        }
        return false;
      },
    };
  },
});

// Type definitions
type Awareness = NonNullable<HocuspocusProvider["awareness"]>;

/** Caret identity a collaborative editor publishes through awareness. */
export interface CollaborationEditorUser {
  name: string;
  color: string;
}

/**
 * Extensions shared by the collaborative and the non-collaborative editor.
 * Exported so the sectioned editor builds identical section editors instead of
 * drifting from the single-document editor.
 *
 * `allowFrontMatter` only decides whether a paste at the very start of *this*
 * editor may become a metadata card: the legacy document and the first section
 * are the document head, later sections are not. The card nodes themselves are
 * always registered so remote or historical cards keep rendering everywhere.
 */
export function createBaseEditorExtensions({
  allowFrontMatter,
}: {
  allowFrontMatter: MarkdownPasteOptions["allowFrontMatter"];
}) {
  return [
    Table.configure({ resizable: false }),
    TableRow,
    TableHeader,
    TableCell,
    BulletList,
    OrderedList,
    ListItem,
    TaskList,
    TaskItem.configure({ nested: true }),
    ListKeymap,
    EnhancedCodeBlock.configure({
      lowlight,
    }),
    Link.configure({
      openOnClick: false,
      autolink: true,
    }),
    Image,
    ...createFrontMatterExtensions(),
    MarkdownPaste.configure({ allowFrontMatter }),
    tabCatcher,
    EnsureTableCellContent,
  ];
}

/**
 * Collaborative extension set for one Y.Doc.
 *
 * `undoRedo` stays off because the Collaboration extension owns undo through the
 * Yjs history, and `field` is pinned to the `default` fragment that both the
 * legacy single document and every section body use.
 */
export function createCollaborativeExtensions({
  document,
  provider,
  user,
  allowFrontMatter,
}: {
  document: NonNullable<ConstructorParameters<typeof HocuspocusProvider>[0]["document"]>;
  provider: HocuspocusProvider;
  user: CollaborationEditorUser;
  allowFrontMatter: MarkdownPasteOptions["allowFrontMatter"];
}) {
  return [
    StarterKit.configure({ ...SHARED_STARTERKIT_OPTIONS, undoRedo: false }),
    Collaboration.configure({ document, field: "default" }),
    CollaborationCaret.configure({ provider, user }),
    ...createBaseEditorExtensions({ allowFrontMatter }),
  ];
}

// HocuspocusProvider calls this on every (re)connect, so the token is always
// re-fetched fresh (and refreshed if near expiry) instead of being captured once.
// Module-level constant keeps a stable identity for the default case.
const ANONYMOUS_TOKEN_PROVIDER = async (): Promise<string> => ANONYMOUS_COLLABORATION_TOKEN;

export type EditorToolbarMode = "inline" | "sticky-main";

interface CollaborativeEditorProps {
  documentId: string;
  className?: string;
  canEdit?: boolean;
  collaborationTokenProvider?: () => Promise<CollaborationCredential>;
  toolbarMode?: EditorToolbarMode;
  /**
   * Seam for U7: the still-live Y.Doc holding content the server refused,
   * handed over before the editor turns read-only.
   */
  onRejectedChanges?: (snapshot: RejectedChangeSnapshot) => void;
}

export default function CollaborativeEditor({
  documentId,
  className,
  canEdit = true,
  collaborationTokenProvider = ANONYMOUS_TOKEN_PROVIDER,
  toolbarMode = "inline",
  onRejectedChanges,
}: CollaborativeEditorProps) {
  const providerBundleRef = useRef<CollaborationProviderBundle | null>(null);
  const [provider, setProvider] = useState<HocuspocusProvider | null>(null);
  const [users, setUsers] = useState<CollaborativeUser[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);
  /** The user's own read/write toggle, not a transport or server fact. */
  const [isEditableMode, setIsEditableMode] = useState(canEdit);
  const [state, dispatch] = useReducer(
    collaborationEditorReducer,
    canEdit,
    createCollaborationEditorState
  );
  const currentUser = useRef(createRandomUser());
  /** The refused content this tab is still holding, for the U7 recovery offer. */
  const [rejectedSnapshot, setRejectedSnapshot] = useState<RejectedChangeSnapshot | null>(null);

  /**
   * A terminal answer that arrived mid-composition. Held until the composition
   * ends so the finished text is part of what recovery captures.
   */
  const deferredTerminalRef = useRef<CollaborationCloseInfo | null>(null);
  const isComposingRef = useLatestRef(state.isComposing);

  /**
   * Hands the refused content to U7 while the Y.Doc is untouched. Called before
   * the editor is made read-only, never after: the Y.Doc is the only place the
   * rejected text exists.
   */
  const captureRejectedChanges = (
    close: CollaborationCloseInfo,
    { includeComposition }: { includeComposition: boolean }
  ) => {
    const bundle = providerBundleRef.current;
    if (!bundle) return;

    const hasPendingChanges = bundle.provider.hasUnsyncedChanges;
    if (!hasPendingChanges && !includeComposition) return;

    const captured: RejectedChangeSnapshot = {
      scope: "document",
      classification: close.classification,
      capturedAt: Date.now(),
      rooms: [
        {
          sectionId: null,
          roomName: bundle.effectiveRoomName,
          document: bundle.provider.document,
          hasPendingChanges,
        },
      ],
    };
    onRejectedChanges?.(captured);
    // The recovery hook serializes from here, after this render, while the
    // editor is still mounted and still holding the refused text.
    setRejectedSnapshot(captured);
  };
  const captureRejectedChangesRef = useLatestRef(captureRejectedChanges);

  const handleCompositionStart = () => dispatch({ type: "compositionStart" });

  const handleCompositionEnd = () => {
    const deferred = deferredTerminalRef.current;
    if (deferred) {
      deferredTerminalRef.current = null;
      captureRejectedChanges(deferred, { includeComposition: true });
    }
    dispatch({ type: "compositionEnd" });
  };

  const syncAwarenessUsers = (awareness?: Awareness | null) => {
    if (!awareness) {
      setUsers([]);
      return;
    }

    const activeUsers = Array.from(awareness.getStates() as Map<number, Record<string, unknown>>)
      .filter(([, state]) => state?.user !== undefined)
      .map(([clientId, state]) => ({
        clientId,
        user: state.user as { name: string; color: string },
        cursor: state?.cursor as { anchor: number; head: number } | undefined,
      })) as CollaborativeUser[];

    setUsers(activeUsers);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: confirmed
  useEffect(() => {
    const existingProvider = providerBundleRef.current;

    if (existingProvider) {
      providerBundleRef.current = null;
      existingProvider.destroy();
    }

    // 새 Provider를 만들기에 앞서 상태를 초기화한다
    setIsInitialized(false);
    setProvider(null);
    setUsers([]);
    setLastSyncedAt(null);
    deferredTerminalRef.current = null;
    // An authoritative reload: the previous document's recovery payload is
    // wiped with the provider that produced it.
    setRejectedSnapshot(null);
    dispatch({ type: "reset", canEdit });

    const handleDisconnect = () => {
      dispatch({ type: "transportLost" });
    };

    const handleAuthenticated = (authenticatedProvider: HocuspocusProvider) => {
      authenticatedProvider.setAwarenessField("user", {
        name: currentUser.current.name,
        color: currentUser.current.color,
      });
    };

    const handleSynced = () => {
      dispatch({ type: "synced" });
    };

    const handleConnectionTimeout = () => {
      console.warn("⚠️ Collaboration server connection is still pending");
      dispatch({ type: "transportUnreachable" });
    };

    /**
     * A logical room close. Only an authorization answer ends editing; a plain
     * transport close is reported through the transport axis and retried.
     */
    const handleClose = (info: CollaborationCloseInfo) => {
      if (info.classification === "detached") return;

      if (info.terminal && info.authorization) {
        // Admission is already blocked by the provider. A composition in flight
        // finishes into the local Y.Doc first, and is captured when it ends.
        if (isComposingRef.current) {
          deferredTerminalRef.current = info;
        } else {
          captureRejectedChangesRef.current(info, { includeComposition: false });
        }
      }

      dispatch({ type: "closed", close: info });
    };

    const handleAwarenessChange = () => {
      syncAwarenessUsers(providerBundleRef.current?.provider.awareness);
    };

    const providerBundle = createCollaborationProvider({
      url: buildCollaborationUrl(import.meta.env.VITE_WS_URL || "ws://localhost:8080", documentId),
      documentId,
      tokenProvider: collaborationTokenProvider,
      onDisconnect: handleDisconnect,
      onAuthenticated: handleAuthenticated,
      onSynced: handleSynced,
      onAwarenessChange: handleAwarenessChange,
      onConnectionTimeout: handleConnectionTimeout,
      onClose: handleClose,
    });

    const hocuspocusProvider = providerBundle.provider;

    providerBundleRef.current = providerBundle;

    setProvider(hocuspocusProvider);
    setIsInitialized(true);
    providerBundle.connect().catch((error) => {
      console.error("❌ Failed to connect to Hocuspocus server", error);
      dispatch({ type: "transportLost" });
    });

    return () => {
      if (providerBundleRef.current === providerBundle) {
        providerBundleRef.current = null;
      }

      providerBundle.destroy();
    };
  }, [documentId, collaborationTokenProvider]);

  useEffect(() => {
    setIsEditableMode(canEdit);
  }, [canEdit]);

  // Permission is one axis: the document ACL the app passes in and the user's
  // own read/write toggle. Neither is a transport fact.
  useEffect(() => {
    dispatch({ type: "permissionChanged", canEdit: canEdit && isEditableMode });
  }, [canEdit, isEditableMode]);

  useEffect(() => {
    if (!provider) {
      return;
    }

    const markSynced = () => {
      setLastSyncedAt(new Date());
      dispatch({ type: "unsyncedChanges", count: 0 });
    };

    const handleUnsyncedChanges = (count: number) => {
      if (count > 0) {
        dispatch({ type: "unsyncedChanges", count });
        return;
      }

      markSynced();
    };

    const handleSynced = ({ state }: { state: boolean }) => {
      if (!state) {
        return;
      }

      if (!provider.hasUnsyncedChanges) {
        markSynced();
      }
    };

    provider.on("unsyncedChanges", handleUnsyncedChanges);
    provider.on("synced", handleSynced);

    if (provider.synced && !provider.hasUnsyncedChanges) {
      markSynced();
    }

    handleUnsyncedChanges(provider.hasUnsyncedChanges ? 1 : 0);

    return () => {
      provider.off("unsyncedChanges", handleUnsyncedChanges);
      provider.off("synced", handleSynced);
    };
  }, [provider]);

  // 에디터 확장 기능들을 메모이제이션
  const extensions = useMemo(() => {
    if (!provider || !isInitialized) {
      // 비협업 모드: StarterKit의 기본 undoRedo 사용
      return [
        StarterKit.configure({ ...SHARED_STARTERKIT_OPTIONS }),
        // A legacy document is a single document head, so its start always
        // accepts front matter.
        ...createBaseEditorExtensions({ allowFrontMatter: true }),
      ];
    }

    // 협업 모드: undoRedo 비활성화, Collaboration extension의 Y.js history 사용
    return createCollaborativeExtensions({
      document: provider.document,
      provider,
      user: {
        name: currentUser.current.name,
        color: currentUser.current.color,
      },
      allowFrontMatter: true,
    });
  }, [provider, isInitialized]);

  const isStickyMainToolbar = toolbarMode === "sticky-main";
  const editorScrollMargin = isStickyMainToolbar ? 128 : 0;

  // 에디터 클래스 문자열 — className 변경 시 에디터를 재생성하지 않고 별도 effect로 갱신
  const editorClass = useMemo(
    () =>
      cn(
        "prose prose-neutral dark:prose-invert prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none max-w-none",
        "min-h-[500px] p-6",
        className
      ),
    [className]
  );

  // Authorization only. A reconnect after the first sync keeps this true, so
  // the editor never loses `contenteditable`, focus, selection or composition.
  const canEditCurrentDocument = isCollaborativeEditorEditable(state);
  const status = collaborationEditorStatus(state);
  const terminalAlert = collaborationTerminalAlert(state);

  useUnloadWarning(shouldWarnBeforeUnload(state));

  const editor = useEditor(
    {
      extensions,
      editorProps: {
        attributes: {
          class: editorClass,
        },
        scrollMargin: editorScrollMargin,
      },
      editable: canEditCurrentDocument,
    },
    [extensions, editorScrollMargin]
  );

  useEffect(() => {
    if (!editor || editor.isDestroyed) {
      return;
    }

    editor.setEditable(canEditCurrentDocument);
  }, [editor, canEditCurrentDocument]);

  // className(→editorClass) 변경 시 에디터 인스턴스/Y.js 바인딩을 유지한 채 클래스만 갱신
  useEffect(() => {
    if (!editor || editor.isDestroyed) {
      return;
    }
    editor.setOptions({
      editorProps: {
        attributes: {
          class: editorClass,
        },
        scrollMargin: editorScrollMargin,
      },
    });
  }, [editor, editorClass, editorScrollMargin]);

  // A legacy document is one room and that room holds the text, so recovery
  // reads the current editor JSON. Nothing is fetched: the editor outlives the
  // terminal close precisely so this stays readable.
  const recovery = useCollaborationRecovery({
    documentId,
    snapshot: rejectedSnapshot,
    hasRootContent: true,
    readContent: () => (editor && !editor.isDestroyed ? editor.getJSON() : null),
  });
  const isRecoveryAvailable = recovery.summary !== null;

  useEffect(() => {
    dispatch({ type: isRecoveryAvailable ? "recoveryCaptured" : "recoveryResolved" });
  }, [isRecoveryAvailable]);

  // 컴포넌트 언마운트 시 에디터 정리
  useEffect(() => {
    return () => {
      if (editor && !editor.isDestroyed) {
        editor.destroy();
      }
    };
  }, [editor]);

  // Sticky: once the document has synced once, the editor stays mounted. A
  // reconnect must not unmount the DOM node the browser holds focus in.
  const isEditorReady = !!provider && isInitialized && state.hasCompletedFirstSync;

  const statusBar = (
    <div className="flex w-full flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap items-center gap-4">
        <ConnectionStatus transport={status.transport} />
        <DocumentStatus
          localChanges={status.localChanges}
          {...(lastSyncedAt ? { lastSyncedAt } : {})}
        />
      </div>
      <UserList users={users} currentUser={currentUser.current} />
    </div>
  );

  const editorMenu = (
    <MenuBar
      editor={editor}
      isEditable={canEditCurrentDocument}
      // The read/write toggle follows authorization, never the socket.
      setIsEditable={
        canEdit && state.hasCompletedFirstSync && !state.terminal ? setIsEditableMode : undefined
      }
      className={isStickyMainToolbar ? "max-w-full bg-background shadow-sm" : undefined}
    />
  );

  const loadingState = (
    <div className="flex items-center justify-center p-8">
      <div className="text-center">
        {status.transport !== "offline" && (
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
        )}
        <p className="text-muted-foreground">
          {status.transport === "offline"
            ? "협업 서버에 연결하지 못했습니다. 계속 재시도 중입니다."
            : "협업 서버에 연결 중..."}
        </p>
      </div>
    </div>
  );

  const editorContent = (
    // Composition is tracked on the wrapper rather than on the editor instance:
    // a terminal denial mid-composition has to wait for `compositionend` before
    // the editor turns read-only, otherwise the 한글 syllable being formed is
    // cancelled by the browser.
    <div
      className="border border-border rounded-lg bg-card"
      onCompositionStart={handleCompositionStart}
      onCompositionEnd={handleCompositionEnd}
    >
      <EditorContent editor={editor} />
    </div>
  );

  const announcements = <CollaborationAnnouncer status={status} terminalAlert={terminalAlert} />;

  const pendingLocalChangesNotice = collaborationLocalChangeState(state) === "pending" && (
    <p className="text-xs text-amber-600 dark:text-amber-400">
      아직 전송되지 않은 변경사항은 현재 탭에만 있습니다. 연결이 복구될 때까지 이 탭을 닫지 마세요.
    </p>
  );

  const recoveryBanner = recovery.summary && (
    <CollaborationRecoveryBanner
      summary={recovery.summary}
      status={recovery.status}
      onCopy={recovery.copy}
      onDownload={recovery.download}
      onDismiss={recovery.dismiss}
    />
  );

  if (isStickyMainToolbar) {
    return (
      <div className="min-h-full w-full">
        <div className="sticky top-0 z-20 border-b bg-background/95 px-6 py-3 backdrop-blur">
          <div className="flex flex-col gap-3">
            {statusBar}
            {announcements}
            {pendingLocalChangesNotice}
            {recoveryBanner}
            {isEditorReady && editorMenu}
          </div>
        </div>
        <div className="px-6 py-4">{isEditorReady ? editorContent : loadingState}</div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-col gap-2 pt-4 px-8">
        {statusBar}
        {announcements}
        {pendingLocalChangesNotice}
        {recoveryBanner}
      </div>

      <div className="w-full">
        {!isEditorReady ? (
          loadingState
        ) : (
          <>
            {editorMenu}
            <div className="mt-4">{editorContent}</div>
          </>
        )}
      </div>
    </div>
  );
}
